/** Shared by both frontends and both Functions runtimes. Times are milliseconds. */
export const TUTOR_PASSWORD_CAMPAIGN_ID = "teacher-password-2026";
// 本番で案内を出し始めた日時（2026-10-21 03:07 JST）。これより前のパスワード更新は今回の変更に数えない
export const TUTOR_PASSWORD_ANNOUNCED_AT = 1792519623378;
// 2026-11-15 23:59:59 JST is the last second of the grace period.
export const TUTOR_PASSWORD_REQUIRED_AT = Date.parse(
  "2026-11-16T00:00:00+09:00"
);
/**
 * 環境ごとに必須化の日時を変えるときの上書き。判定のロジックはどの環境も同じで、違えられるのはこの日付だけ。
 * 今はどの環境も TUTOR_PASSWORD_REQUIRED_AT（staging も本番と同じく 2026-11-15 までが猶予期間）。
 * firestore.rules の tutorPasswordAccess にも同じ値（1794754800000）を直書きしている。変えるときは両方を直す
 */
export const TUTOR_PASSWORD_REQUIRED_AT_BY_PROJECT: Record<string, number> = {};
export const TUTOR_PASSWORD_MESSAGE =
  "8〜64文字で、英字と、数字または記号を含むパスワードを設定してください（スペースと全角文字は使えません）";

/**
 * 使える文字は半角の英数字と記号（スペースを除く ASCII）。日本語キーボードの ¥ も
 * 以前から使えていたので許可する
 */
const TUTOR_PASSWORD_DISALLOWED = /[^\x21-\x7E¥]/;
/** 英字以外で使える文字（数字と記号）。英字と、このどちらかを 1 文字以上含める */
const TUTOR_PASSWORD_DIGIT_OR_SYMBOL = /[\x21-\x40\x5B-\x60\x7B-\x7E¥]/;

export function isValidTutorPassword(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length >= 8 &&
    value.length <= 64 &&
    !TUTOR_PASSWORD_DISALLOWED.test(value) &&
    /[A-Za-z]/.test(value) &&
    TUTOR_PASSWORD_DIGIT_OR_SYMBOL.test(value)
  );
}

/**
 * 生徒のパスワードも同じ条件にする。作成・変更するときだけ確かめ、ログインでは確かめない
 * （今のパスワードのまま使い続けられる。先生のような期限・お願い・利用制限は無い）
 */
export function isValidStudentPassword(value: unknown): value is string {
  return isValidTutorPassword(value);
}

/**
 * 判定に使う tutorConfigs の項目。パスワードログインの無効と初回の必須は従来どおりの項目で、
 * 今回の変更の完了（passwordCampaignId・passwordCompletedAt）をそこに足す。
 * roles/{uid} の次にアプリ本体も読む文書なので、判定のための追加の読み取りが要らない。
 * 書くのはサーバーだけ
 */
export type TutorPasswordConfigState = {
  authority?: string; // 権限。オーナー・管理者にはスクール単位の無効が効かない
  passwordLoginDisabled?: boolean; // true なら対象外
  passwordChangeRequired?: boolean; // 初期パスワードのまま。猶予期間中でも変更が必須
  passwordCampaignId?: string; // 変更を済ませたキャンペーンの ID
  passwordCompletedAt?: number;
};
/**
 * 判定に使う schoolConfigs の項目。スーパー管理人がスクール単位でパスワードログインを無効にする
 * （導入スクール管理画面）。オーナー・管理者以外の先生は、先生ごとの passwordLoginDisabled と同じく対象外になる。
 * schoolConfigs はアプリ本体もログイン直後に読む文書で、クライアントの監視にはアプリ本体が相乗りする
 */
export type TutorPasswordSchoolState = {
  tutorPasswordLoginDisabled?: boolean; // true ならオーナー・管理者以外のスタッフのパスワードログインを無効にする
};

/**
 * スクール単位の無効が効かない権限（オーナー・管理者）。この人たちが Google / Microsoft アカウントを
 * 紐づけていないと、スクールの誰もログインできず、スタッフの設定も直せなくなるので、パスワードでの
 * ログインを残す。firestore.rules の tutorPasswordLoginDisabledBySchool にも同じ値を直書きしている
 */
export const SCHOOL_PASSWORD_LOGIN_EXEMPT_AUTHORITIES: readonly string[] = [
  "owner",
  "admin"
];

/** スクール単位のパスワードログインの無効が、この権限の先生に効くか（オーナー・管理者には効かない） */
export function isPasswordLoginDisabledBySchool(
  authority: string | null | undefined,
  school: TutorPasswordSchoolState | null | undefined
): boolean {
  return (
    school?.tutorPasswordLoginDisabled === true &&
    !SCHOOL_PASSWORD_LOGIN_EXEMPT_AUTHORITIES.includes(authority ?? "")
  );
}
export type TutorPasswordStatus = {
  uid: string;
  disabled: boolean;
  initialRequired: boolean;
  completed: boolean;
  required: boolean;
  showBanner: boolean;
  announcedAt: number;
  requiredAt: number;
  /** 判定した時刻。サーバーで判定したときはサーバーの時計 */
  serverNow: number;
};
export type TutorPasswordSupport = {
  name: string;
  schoolName: string;
  contactUrl: string | null;
  useChannelTalk: boolean;
  topics: { id: string; label: string; url: string; timestamp: number }[];
};

/** その環境の必須化の日時 */
export function tutorPasswordRequiredAtForProject(
  projectId: string | undefined | null
): number {
  return (
    TUTOR_PASSWORD_REQUIRED_AT_BY_PROJECT[projectId ?? ""] ??
    TUTOR_PASSWORD_REQUIRED_AT
  );
}

/** roles/{uid}.ref（schools/{schoolId}/tutors/{uid}）から tutorConfigs のパスを作る */
export function tutorConfigPathOfTutorPath(tutorPath: string): string | null {
  const parts = tutorPath.split("/");
  if (parts.length !== 4 || parts[0] !== "schools" || parts[2] !== "tutors")
    return null;
  return `schools/${parts[1]}/tutorConfigs/${parts[3]}`;
}

/** roles/{uid}.ref（schools/{schoolId}/tutors/{uid}）から schoolConfigs のパスを作る */
export function schoolConfigPathOfTutorPath(tutorPath: string): string | null {
  const parts = tutorPath.split("/");
  if (parts.length !== 4 || parts[0] !== "schools" || parts[2] !== "tutors")
    return null;
  return `schoolConfigs/${parts[1]}`;
}

/**
 * tutorConfigs の項目（とスクールの設定）から状態を判定する。firestore.rules の
 * tutorPasswordAccess・tutorPasswordLoginDisabledBySchool と同じ条件。文書が無い（null）先生は
 * 「まだ変えていない」扱い。school を渡さないときはスクール単位の無効を見ない
 */
export function evaluateTutorPasswordPolicy(
  config: TutorPasswordConfigState | null | undefined,
  now: number,
  requiredAt = TUTOR_PASSWORD_REQUIRED_AT,
  school?: TutorPasswordSchoolState | null
): Omit<TutorPasswordStatus, "uid"> {
  const disabled =
    config?.passwordLoginDisabled === true ||
    isPasswordLoginDisabledBySchool(config?.authority, school);
  const initialRequired = !disabled && config?.passwordChangeRequired === true;
  const completed = config?.passwordCampaignId === TUTOR_PASSWORD_CAMPAIGN_ID;
  const announced = now >= TUTOR_PASSWORD_ANNOUNCED_AT;
  return {
    disabled,
    initialRequired,
    completed,
    announcedAt: TUTOR_PASSWORD_ANNOUNCED_AT,
    requiredAt,
    serverNow: now,
    required:
      !disabled &&
      (initialRequired || (announced && !completed && now >= requiredAt)),
    showBanner:
      !disabled &&
      !initialRequired &&
      !completed &&
      announced &&
      now < requiredAt
  };
}
